import { Injectable } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import enAiConnector from '@assets/i18n/ai-connector/en';
import esAiConnector from '@assets/i18n/ai-connector/es';
import enAppLinkRegister from '@assets/i18n/app-link-register/en';
import esAppLinkRegister from '@assets/i18n/app-link-register/es';
import enAuth from '@assets/i18n/auth/en';
import esAuth from '@assets/i18n/auth/es';
import enChat from '@assets/i18n/chat/en';
import esChat from '@assets/i18n/chat/es';
import enCommon from '@assets/i18n/common/en';
import esCommon from '@assets/i18n/common/es';
import enCompanies from '@assets/i18n/companies/en';
import esCompanies from '@assets/i18n/companies/es';
import enDashboard from '@assets/i18n/dashboard/en';
import esDashboard from '@assets/i18n/dashboard/es';
import enEntityLocation from '@assets/i18n/entity-location/en';
import esEntityLocation from '@assets/i18n/entity-location/es';
import enErrors from '@assets/i18n/errors/en';
import esErrors from '@assets/i18n/errors/es';
import enIndividualUserManagement from '@assets/i18n/individual-user-management/en';
import esIndividualUserManagement from '@assets/i18n/individual-user-management/es';
import enLanding from '@assets/i18n/landing/en';
import esLanding from '@assets/i18n/landing/es';
import enLandingCompany from '@assets/i18n/landing-company/en';
import esLandingCompany from '@assets/i18n/landing-company/es';
import enLayout from '@assets/i18n/layout/en';
import esLayout from '@assets/i18n/layout/es';
import enNavigation from '@assets/i18n/navigation/en';
import esNavigation from '@assets/i18n/navigation/es';
import enOffers from '@assets/i18n/offers/en';
import esOffers from '@assets/i18n/offers/es';
import enPublicSearch from '@assets/i18n/public-search/en';
import esPublicSearch from '@assets/i18n/public-search/es';
import enSaasTraining from '@assets/i18n/saas-training/en';
import esSaasTraining from '@assets/i18n/saas-training/es';
import enTiktok from '@assets/i18n/tiktok/en';
import esTiktok from '@assets/i18n/tiktok/es';
import enUserActionLog from '@assets/i18n/user-action-log/en';
import esUserActionLog from '@assets/i18n/user-action-log/es';
import enUserManagement from '@assets/i18n/user-management/en';
import esUserManagement from '@assets/i18n/user-management/es';
import enWokapIa from '@assets/i18n/wokap-ia/en';
import esWokapIa from '@assets/i18n/wokap-ia/es';

interface TranslationDictionary {
  [key: string]: string | TranslationDictionary;
}

// Define modules and supported languages
type TranslationModuleKey =
  | 'aiConnector'
  | 'appLinkRegister'
  | 'landingCompany'
  | 'publicSearch'
  | 'auth'
  | 'common'
  | 'chat'
  | 'companies'
  | 'dashboard'
  | 'errors'
  | 'individualUserManagement'
  | 'landing'
  | 'layout'
  | 'navigation'
  | 'offers'
  | 'tiktok'
  | 'userManagement'
  | 'entityLocation'
  | 'userActionLog'
  | 'wokapIa'
  | 'saasTraining';

type TranslationModules = {
  [K in TranslationModuleKey]: TranslationDictionary;
};

type SupportedLanguages = 'es' | 'en';

// Translation modules mapping with proper typing
const TS_TRANSLATIONS: Record<SupportedLanguages, TranslationModules> = {
  es: {
    aiConnector: esAiConnector,
    appLinkRegister: esAppLinkRegister,
    landingCompany: esLandingCompany,
    publicSearch: esPublicSearch,
    auth: esAuth,
    common: esCommon,
    chat: esChat,
    companies: esCompanies,
    dashboard: esDashboard,
    errors: esErrors,
    landing: esLanding,
    layout: esLayout,
    navigation: esNavigation,
    offers: esOffers,
    tiktok: esTiktok,
    userManagement: esUserManagement,
    individualUserManagement: esIndividualUserManagement,
    entityLocation: esEntityLocation,
    userActionLog: esUserActionLog,
    wokapIa: esWokapIa,
    saasTraining: esSaasTraining,
  },
  en: {
    aiConnector: enAiConnector,
    appLinkRegister: enAppLinkRegister,
    landingCompany: enLandingCompany,
    publicSearch: enPublicSearch,
    auth: enAuth,
    common: enCommon,
    chat: enChat,
    companies: enCompanies,
    dashboard: enDashboard,
    errors: enErrors,
    individualUserManagement: enIndividualUserManagement,
    landing: enLanding,
    layout: enLayout,
    navigation: enNavigation,
    offers: enOffers,
    tiktok: enTiktok,
    userManagement: enUserManagement,
    entityLocation: enEntityLocation,
    userActionLog: enUserActionLog,
    wokapIa: enWokapIa,
    saasTraining: enSaasTraining,
  },
};

// Default language to fall back to if requested language isn't supported
const DEFAULT_LANGUAGE: SupportedLanguages = 'es';

@Injectable()
export class TranslationLoader implements TranslateLoader {
  private _cachedTranslations: Record<SupportedLanguages, TranslationDictionary> = {} as Record<SupportedLanguages, TranslationDictionary>;

  /**
   * Required method by the TranslateLoader interface
   * Returns the combined translations for a specific language
   * @param lang Language to load
   */
  getTranslation(lang: string): Observable<TranslationDictionary> {
    return of(this.getTypeScriptTranslations(lang));
  }

  /**
   * Gets translations from TypeScript files
   * Uses caching to improve performance on subsequent calls
   * @param lang Language to load
   */
  private getTypeScriptTranslations(lang: string): TranslationDictionary {
    // Check if language is supported, otherwise use default
    const supportedLang = this.getSupportedLanguage(lang);

    // Return cached translations if available
    if (this._cachedTranslations[supportedLang]) {
      return this._cachedTranslations[supportedLang];
    }

    // Get language translations
    const langTranslations = TS_TRANSLATIONS[supportedLang];

    // Combine all translations from TS into a single object
    const translations = this.mergeTranslations(langTranslations);

    // Cache the result
    this._cachedTranslations[supportedLang] = translations;

    return translations;
  }

  /**
   * Checks if the language is supported and returns the supported language or default
   * @param lang Language to check
   */
  private getSupportedLanguage(lang: string): SupportedLanguages {
    return lang in TS_TRANSLATIONS ? lang as SupportedLanguages : DEFAULT_LANGUAGE;
  }

  /**
   * Efficiently merges all translation modules into a single dictionary
   * @param modules Translation modules to merge
   */
  private mergeTranslations(modules: TranslationModules): TranslationDictionary {
    // Use a more efficient deep merge approach with a single object
    return Object.keys(modules).reduce<TranslationDictionary>((acc, moduleName) => {
      const moduleKey = moduleName as TranslationModuleKey;

      return { ...acc, ...modules[moduleKey] };
    }, {});
  }
}
